import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SommChat from "@/components/SommChat";
import { GuideDownloadCTA } from "@/components/GuideDownloadCTA";
import { Eye, Wind, GlassWater, ArrowLeft, Grape, Sparkles, Lightbulb, ChevronRight } from "lucide-react";

const C = { bg: "#F7F4EF", borgona: "#5A1E2C", oro: "#C8A96A", vino: "#8B2635", texto: "#222222", gris: "#777777" };

const FASES = [
  {
    icon: Eye,
    n: "01",
    titulo: "Fase Visual",
    intro: "El primer contacto con el vino entra por los ojos. Inclina la copa unos 45° sobre un fondo blanco y con buena luz. La vista te adelanta la edad, el estado y hasta el cuerpo del vino.",
    puntos: [
      ["Color e intensidad", "Los blancos van de amarillo pálido a dorado; los tintos, de púrpura y rubí (jóvenes) a granate y teja (evolucionados). Un color más intenso suele indicar más concentración."],
      ["Limpidez y brillo", "Un vino sano se ve limpio y brillante. Turbidez o falta de brillo pueden avisar de un defecto (salvo en vinos sin filtrar)."],
      ["Ribete", "Es el borde del vino en la copa. En tintos, un ribete violáceo indica juventud; tonos anaranjados o teja, evolución."],
      ["Lágrima", "Al girar la copa, las gotas que resbalan por las paredes. Lágrimas densas y lentas sugieren más alcohol y/o azúcar (más glicerina)."],
    ],
    consejo: "Gira suavemente la copa antes de mirar: verás mejor la lágrima y prepararás los aromas para la siguiente fase.",
  },
  {
    icon: Wind,
    n: "02",
    titulo: "Fase Olfativa",
    intro: "Aquí está gran parte de la información. Huele primero el vino en reposo (‘primera nariz’) y después de girar la copa (‘segunda nariz’), cuando se liberan más aromas. No hay respuestas incorrectas: describe lo que te recuerde.",
    puntos: [
      ["Intensidad", "¿Los aromas son discretos o saltan de la copa? Va de baja a muy alta."],
      ["Aromas primarios", "Vienen de la uva y el terreno: frutas, flores, hierbas, notas minerales. Marcan el carácter varietal."],
      ["Aromas secundarios", "Nacen de la fermentación y la crianza: levadura, pan, mantequilla, lácteos, notas de barrica como vainilla o coco."],
      ["Aromas terciarios", "Aparecen con el tiempo (el ‘bouquet’ de guarda): cuero, tabaco, frutos secos, setas, especias, cacao."],
      ["Limpieza", "Un vino ‘limpio’ no tiene olores raros. Aromas a corcho, vinagre o huevo podrido indican defectos."],
    ],
    consejo: "Acércate a la copa con inspiraciones cortas. Si te satura, huele tu antebrazo unos segundos para ‘resetear’ el olfato.",
  },
  {
    icon: GlassWater,
    n: "03",
    titulo: "Fase Gustativa",
    intro: "Da un sorbo moderado y muévelo por toda la boca para que llegue a todas las papilas. Aquí confirmas lo que anticiparon la vista y el olfato, y valoras el equilibrio del conjunto.",
    puntos: [
      ["Dulzor", "De seco (nada de azúcar percibido) a dulce. La mayoría de los vinos de mesa son secos."],
      ["Acidez", "La frescura que hace la boca agua. Aporta viveza; en exceso resulta agresiva, en defecto el vino se nota ‘plano’."],
      ["Taninos", "Sensación de aspereza/sequedad, típica de los tintos (vienen de hollejos, pepitas y barrica). Bien integrados son sedosos; verdes o duros, ásperos."],
      ["Cuerpo", "El ‘peso’ o densidad del vino en boca: ligero, medio o robusto."],
      ["Equilibrio y final", "Un buen vino tiene sus componentes en armonía. El ‘final’ o retrogusto es lo que persiste tras tragar: cuanto más largo y agradable, mayor calidad."],
    ],
    consejo: "Cuenta los segundos que dura el sabor tras tragar (las ‘caudalías’): un final largo suele ser señal de un gran vino.",
  },
];

const GLOSARIO = [
  ["Acidez", "Sensación de frescura y viveza en boca, similar a morder una manzana verde. Da equilibrio y capacidad de guarda al vino."],
  ["Aromas primarios", "Olores que proceden de la propia uva y su origen: frutas, flores, hierbas y notas minerales."],
  ["Aromas secundarios", "Olores generados durante la fermentación y la crianza: levadura, pan, mantequilla, vainilla, coco (barrica)."],
  ["Aromas terciarios", "Olores que se desarrollan con el envejecimiento (el ‘bouquet’): cuero, tabaco, frutos secos, setas, cacao."],
  ["Astringencia", "Sensación de sequedad y aspereza en encías y lengua producida por los taninos, como la de un té muy cargado."],
  ["Caudalías", "Unidad para medir la persistencia: los segundos que el sabor permanece en boca después de tragar."],
  ["Crianza", "Periodo de envejecimiento del vino, habitualmente en barrica de roble y/o botella, que aporta aromas y suaviza los taninos."],
  ["Cuerpo", "El ‘peso’ o densidad que el vino tiene en boca. Puede ser ligero, medio o robusto."],
  ["Cata a ciegas", "Degustación sin conocer la etiqueta, para juzgar el vino sin prejuicios e intentar deducir variedad, origen y añada."],
  ["Decantar", "Trasvasar el vino a una jarra para airearlo y/o separarlo de sus posos, ayudando a que se exprese mejor."],
  ["Denominación de Origen (DO)", "Sello que certifica que un vino procede de una zona concreta y cumple sus normas de elaboración."],
  ["Dulzor", "Cantidad de azúcar percibida. Va de seco (sin azúcar apreciable) a dulce."],
  ["Equilibrio", "Armonía entre los componentes del vino (fruta, acidez, alcohol, taninos). Un vino equilibrado no destaca ninguno de forma molesta."],
  ["Intensidad", "Fuerza con la que se perciben el color, los aromas o los sabores del vino."],
  ["Lágrima", "Gotas que resbalan por la pared de la copa al girarla. Lágrimas densas indican más alcohol y/o azúcar."],
  ["Persistencia", "Duración del sabor y los aromas en boca tras tragar. A mayor persistencia, mayor suele ser la calidad."],
  ["Retrogusto", "El sabor y las sensaciones que quedan en la boca después de tragar el vino, también llamado ‘final’."],
  ["Ribete", "El borde exterior del vino en la copa. Su color revela pistas sobre la edad y evolución del vino."],
  ["Taninos", "Compuestos de hollejos, pepitas y barrica que aportan estructura y esa sensación astringente típica de los tintos."],
  ["Varietal", "Vino elaborado principalmente con una sola variedad de uva, cuyo carácter se refleja en la copa."],
];

export default function Aprende() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const t = norm(q.trim());
    if (!t) return GLOSARIO;
    return GLOSARIO.filter(([term, def]) => norm(term + " " + def).includes(t));
  }, [q]);

  const askSomm = () => {
    if (user) window.dispatchEvent(new Event("vinonotas-open-somm"));
    else navigate("/register");
  };

  return (
    <div style={{ background: C.bg, color: C.texto }} className="min-h-screen font-sans">
      {/* Header */}
      <header className="border-b border-black/5" style={{ background: "#fff" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <button data-testid="aprende-back-btn" onClick={() => navigate(user ? "/app" : "/")} className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-70" style={{ color: C.borgona }}>
            <ArrowLeft className="w-4 h-4" /> {user ? "Volver al panel" : "Inicio"}
          </button>
          <a href={user ? "/app" : "/"} className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: C.borgona }}>
              <Grape className="w-4 h-4" style={{ color: C.oro }} />
            </span>
            <span className="font-serif text-lg" style={{ color: C.borgona }}>Vinonotas</span>
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {/* Intro */}
        <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>Guía de cata</p>
        <h1 className="font-serif text-4xl sm:text-5xl mb-4" style={{ color: C.borgona }}>Aprende a catar</h1>
        <p className="text-lg leading-relaxed max-w-2xl" style={{ color: "#444" }}>
          Catar un vino es mucho más que beberlo: es observarlo, olerlo y saborearlo con atención. Una cata profesional
          se organiza en tres fases. No necesitas ser experto —solo prestar atención y poner palabras a lo que percibes.
        </p>

        <button data-testid="ask-somm-btn" onClick={askSomm}
          className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 text-white text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl"
          style={{ background: C.borgona }}>
          <Sparkles className="w-4 h-4" style={{ color: C.oro }} /> Pregúntale al sumiller
        </button>

        <div className="mt-10">
          <GuideDownloadCTA theme="light" />
        </div>

        {/* Fases */}
        <div className="mt-14 space-y-10">
          {FASES.map((f) => (
            <section key={f.n} data-testid={`fase-${f.n}`} className="rounded-2xl bg-white border border-black/5 overflow-hidden shadow-[0_8px_30px_rgba(90,30,44,0.06)]">
              <div className="flex items-center gap-4 px-6 sm:px-8 py-5" style={{ background: C.borgona }}>
                <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(200,169,106,0.2)" }}>
                  <f.icon className="w-6 h-6" style={{ color: C.oro }} strokeWidth={1.75} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: C.oro }}>Fase {f.n}</p>
                  <h2 className="font-serif text-2xl text-white leading-none mt-0.5">{f.titulo}</h2>
                </div>
              </div>
              <div className="px-6 sm:px-8 py-6">
                <p className="leading-relaxed mb-5" style={{ color: "#444" }}>{f.intro}</p>
                <div className="space-y-3">
                  {f.puntos.map(([t, d]) => (
                    <div key={t} className="flex gap-3">
                      <ChevronRight className="w-4 h-4 mt-1 shrink-0" style={{ color: C.oro }} />
                      <p className="text-[15px] leading-relaxed" style={{ color: "#333" }}>
                        <strong style={{ color: C.texto }}>{t}.</strong> {d}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-start gap-2.5 rounded-xl p-4" style={{ background: C.bg }}>
                  <Lightbulb className="w-5 h-5 mt-0.5 shrink-0" style={{ color: C.vino }} />
                  <p className="text-sm leading-relaxed" style={{ color: "#555" }}><strong style={{ color: C.vino }}>Consejo:</strong> {f.consejo}</p>
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* Glosario */}
        <section className="mt-16">
          <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>Diccionario del catador</p>
          <h2 className="font-serif text-3xl mb-2" style={{ color: C.borgona }}>Glosario de términos</h2>
          <p className="text-[15px] mb-6" style={{ color: C.gris }}>Definiciones claras para entender cualquier ficha de cata.</p>

          <input
            data-testid="glosario-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar término (ej. taninos, lágrima, acidez...)"
            className="w-full max-w-md h-11 rounded-xl border border-black/10 px-4 text-sm mb-6 focus:outline-none focus:border-[#5A1E2C]/50"
          />

          {filtered.length === 0 ? (
            <p className="text-sm" style={{ color: C.gris }}>No se encontraron términos para “{q}”.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {filtered.map(([term, def]) => (
                <div key={term} data-testid={`glosario-item-${term}`} className="rounded-xl bg-white border border-black/5 p-4">
                  <h3 className="font-serif text-lg mb-1" style={{ color: C.vino }}>{term}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#555" }}>{def}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <p className="mt-14 pt-6 border-t border-black/10 text-xs" style={{ color: C.gris }}>
          Vinonotas · Aprende, cata y registra. Bebe con moderación.
        </p>
      </main>

      {user && <SommChat sessionId="aprende-a-catar" />}
    </div>
  );
}
